import { z } from 'zod';
import { zMetricType, zIntegrationProperties, zTag, zTagSelection, zTrainingTypeSchema, } from '../core';
import { zMetricConfig } from '../metrics-library/commands';
export const ExerciseLibraryModule = 'ExerciseLibrary';
const module = z.literal(ExerciseLibraryModule);
const zExerciseBodyPartSchema = z.array(z.object({
    id: z.uuid(),
    sortOrder: z.number(),
    bodyPart: z.object({
        id: z.uuid(),
        name: z.string(),
    }),
}));
const zExerciseEquipmentSchema = z.array(z.object({
    id: z.uuid(),
    sortOrder: z.number(),
    equipmentItem: z.object({
        id: z.uuid(),
        name: z.string(),
    }),
}));
export const BaseExerciseSchema = z.object({
    // Details
    id: z.uuid(),
    name: z.string().min(3, {
        message: 'exerciseNameMinLength',
    }),
    trainingTypeId: z.uuid(),
    imageUrl: z.url().nullable(),
    videoUrl: z.url().nullable(),
    exerciseBodyParts: zExerciseBodyPartSchema,
    exerciseEquipment: zExerciseEquipmentSchema,
    exerciseTags: z.array(zTagSelection),
    trainingType: zTrainingTypeSchema,
    defaultMetrics: z.array(z.object({
        id: z.uuid(),
        sortOrder: z.number(),
        metric: z.object({
            id: z.uuid(),
            name: z.string(),
            type: zMetricType,
            config: zMetricConfig,
        }),
    })),
    integrationProperties: zIntegrationProperties,
    teamId: z.string().nullish(),
});
export const ExerciseDetailsSchema = BaseExerciseSchema.omit({
    id: true,
    exerciseBodyParts: true,
    exerciseEquipment: true,
    exerciseTags: true,
    defaultMetrics: true,
    integrationProperties: true,
    trainingType: true,
    trainingTypeId: true,
}).merge(
// omit the id of the relations to body parts, equipment, and categories
// to allow for easier form submission, instead just pass the id of the constructs
z.object({
    bodyParts: z.array(z.object({
        id: z.uuid(),
        name: z.string(),
    })),
    equipment: z.array(z.object({
        id: z.uuid(),
        name: z.string(),
    })),
    tags: z.array(zTag),
    trainingType: zTrainingTypeSchema.nullable(),
}));
export const ExerciseMetricSelectionSchema = z.object({
    defaultMetrics: z
        .array(z.object({
        id: z.uuid(),
        name: z.string(),
        type: zMetricType,
        config: zMetricConfig,
    }))
        .min(1, { message: 'atLeastOneMetricRequired' }),
    integrationProviderProperties: z.array(z.object({
        provider: z.string(),
        property: z.string(),
        value: z.union([z.string(), z.boolean(), z.number()]),
    })),
});
export function createExerciseDetailsSchema(opts) {
    if (!opts.requireMetadataFields) {
        return ExerciseDetailsSchema;
    }
    return ExerciseDetailsSchema.extend({
        tags: z.array(zTag).min(1, { message: 'atLeastOneTagRequired' }),
        bodyParts: z
            .array(z.object({ id: z.uuid(), name: z.string() }))
            .min(1, { message: 'atLeastOneBodyPartRequired' }),
        equipment: z
            .array(z.object({ id: z.uuid(), name: z.string() }))
            .min(1, { message: 'atLeastOneEquipmentRequired' }),
    });
}
export const ExerciseFormSchema = ExerciseDetailsSchema.merge(ExerciseMetricSelectionSchema);
export const CreateExercise = z.object({
    module,
    type: z.literal('CreateExercise'),
    input: BaseExerciseSchema,
});
export const EditExercise = z.object({
    module,
    type: z.literal('EditExercise'),
    input: BaseExerciseSchema,
});
export const SearchExercises = z.object({
    module,
    type: z.literal('SearchExercises'),
    input: z.object({
        teamId: z.uuid().nullish(),
        searchTerm: z.string().optional(),
        offset: z.number().optional(),
        limit: z.number().positive().optional(),
        trainingTypeIds: z.array(z.uuid()).optional(),
        bodyPartIds: z.array(z.uuid()).optional(),
        equipmentIds: z.array(z.uuid()).optional(),
        tagIds: z.array(z.uuid()).optional(),
        categories: z.array(z.string()).optional(),
        showDefault: z.boolean().optional(),
        /**
         * ID of the prescribed exercise in the alternate picker. When present (and
         * the alternate-suggestions flag is on), results are ranked by movement-tag
         * overlap with this exercise. Only ever sent by the alternate picker.
         */
        baseExerciseId: z.uuid().optional(),
        /**
         * Library exercise IDs to omit from results (the alternate group's members,
         * including the prescribed exercise). Only ever sent by the alternate picker.
         */
        excludeExerciseIds: z.array(z.uuid()).optional(),
        /**
         * Used for the exercise page. We don't want to show the base library exercises on a specific team's exercise library.
         * Override for including base library exercises (exercises with null teamId):
         * - If false: exclude base library exercises regardless of team settings
         * - If true: include base library exercises regardless of team settings
         * - If undefined: use team settings
         */
        includeBaseLibraryExercisesOverride: z.boolean().optional(),
    }),
});
//# sourceMappingURL=commands.js.map